import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import MicIcon from '@mui/icons-material/Mic';
import { useAuth } from '../../contexts/AuthContext';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useWhatsAppConnection } from '../../contexts/WhatsAppConnectionContext';
import { flowService } from '../../services/flowService';
import welcomeChipsService from '../../services/welcomeChipsService';

const MAX_CHIPS = 4;
const MAX_CHIP_LENGTH = 80;
const COUNTER_THRESHOLD = 60;

const emptyRow = () => ({ text: '', flowId: '' });

const SYNC_LABELS = {
  IN_SYNC: { label: 'Live', color: 'success' },
  NOT_IN_SYNC: { label: 'Not in sync', color: 'warning' },
  NOT_READ: { label: 'Not read', color: 'default' },
};

/** Published flows first, then everything else, each labelled by name, never by id (L005). */
const sortFlowsForPicker = (flows) => {
  const list = Array.isArray(flows) ? flows : [];
  return [...list].sort((a, b) => {
    const aPublished = a.status === 'PUBLISHED' ? 0 : 1;
    const bPublished = b.status === 'PUBLISHED' ? 0 : 1;
    if (aPublished !== bPublished) return aPublished - bPublished;
    return (a.name || '').localeCompare(b.name || '');
  });
};

/**
 * Saved chips become rows 1:1. No chips means no rows: a blank row showed up as an empty field
 * already outlined in red, as if the owner had done something wrong by not having any.
 */
const toRows = (chips) => {
  const list = Array.isArray(chips) ? chips : [];
  return list.map((chip) => ({ text: chip.text || '', flowId: chip.flowId || '' }));
};

const normalize = (rows) => rows.map((row) => ({ text: row.text.trim(), flowId: row.flowId || null }));

const duplicateCounts = (rows) => {
  const counts = new Map();
  rows.forEach((row) => {
    const key = row.text.trim().toLowerCase();
    if (!key) return;
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return counts;
};

const rowIsInvalid = (row, counts) => {
  const trimmed = row.text.trim();
  if (!trimmed) return true;
  return (counts.get(trimmed.toLowerCase()) || 0) > 1;
};

const orphanLine = (phrases) => {
  if (!phrases || phrases.length === 0) return null;
  if (phrases.length === 1) return `Trigger for '${phrases[0]}' is still active.`;
  return `Triggers for ${phrases.map((p) => `'${p}'`).join(', ')} are still active.`;
};

/** The Cloud API "secure service" notice WhatsApp shows above the chips. */
const SECURE_SERVICE_NOTICES = [
  'This business uses a secure service from Meta to manage this chat. Tap to learn more.',
];

/**
 * A ~300px WhatsApp chat mock matching Android's real rendering: white header with a lettered
 * avatar, the beige wallpaper, the two Cloud API notices, and the chips rendered live as
 * right-aligned outgoing cards (not pills) with a send icon, stacked above the composer.
 */
const ChipPreview = ({ businessName, chipTexts }) => (
  <Box sx={{ width: 300, maxWidth: '100%', mx: 'auto' }}>
    <Box
      sx={{
        bgcolor: '#111418',
        borderRadius: '28px',
        p: '7px',
        pt: '11px',
        boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
      }}
    >
      <Box sx={{ width: 30, height: 4, mx: 'auto', mb: '6px', borderRadius: 2, bgcolor: 'rgba(255,255,255,0.35)' }} />
      <Box sx={{ borderRadius: '20px', overflow: 'hidden' }}>
        <Box sx={{ height: 44, px: 1, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fff' }}>
          <ArrowBackIcon sx={{ fontSize: 18, color: '#000' }} />
          <Box
            sx={{
              width: 26,
              height: 26,
              flexShrink: 0,
              borderRadius: '50%',
              bgcolor: '#F6C89F',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#A15C2B' }}>
              {(businessName || '?').trim().charAt(0).toUpperCase()}
            </Typography>
          </Box>
          <Typography noWrap sx={{ fontSize: 13, fontWeight: 500, color: '#000', flex: 1 }}>
            {businessName}
          </Typography>
          <MoreVertIcon sx={{ fontSize: 18, color: '#000' }} />
        </Box>
        <Box
          sx={{
            height: 340,
            bgcolor: '#EFEAE2',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.75,
            p: 1,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ bgcolor: '#fff', color: '#54656F', borderRadius: '6px', px: 1, py: '2px', fontSize: 10 }}>
            Today
          </Box>
          {SECURE_SERVICE_NOTICES.map((notice) => (
            <Box
              key={notice}
              sx={{
                bgcolor: '#E1F7CB',
                color: '#333',
                borderRadius: '6px',
                px: 1,
                py: '4px',
                fontSize: '9.5px',
                lineHeight: 1.3,
                textAlign: 'center',
              }}
            >
              {notice}
            </Box>
          ))}

          <Box
            sx={{
              flex: 1,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'flex-end',
              gap: 0.75,
            }}
          >
            {chipTexts.map((text, i) => (
              <Box
                key={`${text}-${i}`}
                sx={{
                  bgcolor: '#fff',
                  color: '#111418',
                  borderRadius: '13px',
                  py: '14px',
                  px: '18px',
                  fontSize: 13,
                  maxWidth: '85%',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Box component="span" sx={{ wordBreak: 'break-word' }}>{text}</Box>
                <SendOutlinedIcon sx={{ fontSize: 16, color: '#25D366', flexShrink: 0 }} />
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ height: 44, bgcolor: '#EFEAE2', display: 'flex', alignItems: 'center', gap: 0.75, px: 1 }}>
          <Box
            sx={{
              flex: 1,
              height: 30,
              bgcolor: '#fff',
              borderRadius: '15px',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1,
            }}
          >
            <EmojiEmotionsOutlinedIcon sx={{ fontSize: 16, color: '#8696A0' }} />
            <Typography sx={{ fontSize: 12, color: '#8696A0', flex: 1 }}>Message</Typography>
            <AttachFileIcon sx={{ fontSize: 16, color: '#8696A0' }} />
            <CameraAltOutlinedIcon sx={{ fontSize: 16, color: '#8696A0' }} />
          </Box>
          <Box
            sx={{
              width: 30,
              height: 30,
              flexShrink: 0,
              borderRadius: '50%',
              bgcolor: '#25D366',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MicIcon sx={{ fontSize: 16, color: '#fff' }} />
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
);

/**
 * Settings -> Welcome chips.
 *
 * Up to four WhatsApp "ice breaker" prompts a first-time visitor sees before they type. Each one
 * is wired to a keyword trigger on save, so tapping it sends a flow exactly like typing the
 * phrase would.
 */
const WelcomeChipsSettings = () => {
  const { authenticatedFetch } = useAuth();
  const { selectedEnvironment } = useEnvironment();
  const { selectedConnection } = useWhatsAppConnection();
  const envId = selectedEnvironment?.id;
  const businessName = selectedConnection?.wabaName || 'Your business';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [flows, setFlows] = useState([]);
  const [rows, setRows] = useState([]);
  const [baseline, setBaseline] = useState([]);
  const [status, setStatus] = useState(null);

  const sortedFlows = useMemo(() => sortFlowsForPicker(flows), [flows]);

  const applyStatus = useCallback((result) => {
    setStatus(result);
    const nextRows = toRows(result?.chips);
    setRows(nextRows);
    setBaseline(nextRows);
  }, []);

  const load = useCallback(async () => {
    if (!envId) return;
    setLoading(true);
    setError(null);
    try {
      const [flowList, chipsStatus] = await Promise.all([
        flowService.listFlows(envId, authenticatedFetch).catch(() => []),
        welcomeChipsService.get(envId, authenticatedFetch),
      ]);
      setFlows(flowList);
      applyStatus(chipsStatus);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, envId, applyStatus]);

  useEffect(() => { load(); }, [load]);

  const setRow = (index, changes) => {
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...changes } : row)));
  };

  const removeRow = (index) => {
    setRows((current) => current.filter((_, i) => i !== index));
  };

  const addRow = () => {
    setRows((current) => [...current, emptyRow()]);
  };

  const counts = useMemo(() => duplicateCounts(rows), [rows]);
  const hasInvalidRow = rows.some((row) => rowIsInvalid(row, counts));
  const unchanged = JSON.stringify(normalize(rows)) === JSON.stringify(normalize(baseline));
  const canSave = !saving && !hasInvalidRow && !unchanged;
  const atLimit = rows.length >= MAX_CHIPS;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const chips = normalize(rows);
      const result = await welcomeChipsService.save(envId, chips, authenticatedFetch);
      applyStatus(result);
      setNotice('Welcome chips saved.');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!envId) {
    return <Alert severity="info">Select an environment to set up welcome chips.</Alert>;
  }
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  const syncInfo = status?.syncStatus ? SYNC_LABELS[status.syncStatus] : null;
  const orphanNote = orphanLine(status?.orphanedTriggers);
  const chipTexts = rows.map((row) => row.text.trim()).filter(Boolean);

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Welcome chips</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Shown once, when someone opens a chat with your number for the first time.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={7} sx={{ order: { xs: 2, md: 1 } }}>
              <Stack spacing={1.5}>
                {rows.map((row, index) => {
                  const invalid = rowIsInvalid(row, counts);
                  const showCounter = row.text.length > COUNTER_THRESHOLD;
                  return (
                    <Stack direction="row" spacing={1} alignItems="flex-start" key={index}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Chip text"
                        value={row.text}
                        error={invalid}
                        onChange={(e) => setRow(index, { text: e.target.value.slice(0, MAX_CHIP_LENGTH) })}
                        helperText={showCounter ? `${row.text.length}/${MAX_CHIP_LENGTH}` : ' '}
                        inputProps={{ maxLength: MAX_CHIP_LENGTH }}
                      />
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <Select
                          value={row.flowId}
                          onChange={(e) => setRow(index, { flowId: e.target.value })}
                          displayEmpty
                          aria-label="Sends flow"
                        >
                          <MenuItem value="">Just send the text</MenuItem>
                          {sortedFlows.map((flow) => (
                            <MenuItem key={flow.id} value={flow.id}>
                              {flow.name}{flow.status === 'PUBLISHED' ? '' : ' (not published)'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <IconButton
                        aria-label={`Remove chip ${index + 1}`}
                        onClick={() => removeRow(index)}
                        size="small"
                        sx={{ mt: 0.5 }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  );
                })}

                <Box>
                  <Tooltip title={atLimit ? 'Up to four chips' : ''}>
                    <span>
                      <Button startIcon={<AddIcon />} onClick={addRow} disabled={atLimit}>
                        Add chip
                      </Button>
                    </span>
                  </Tooltip>
                </Box>

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Button variant="contained" onClick={handleSave} disabled={!canSave}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                  {syncInfo && <Chip size="small" color={syncInfo.color} label={syncInfo.label} />}
                </Stack>

                {status?.lastError && <Alert severity="error">{status.lastError}</Alert>}
                {orphanNote && (
                  <Typography variant="body2" color="text.secondary">{orphanNote}</Typography>
                )}
              </Stack>
            </Grid>

            <Grid item xs={12} md={5} sx={{ order: { xs: 1, md: 2 } }}>
              <ChipPreview businessName={businessName} chipTexts={chipTexts} />
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5, textAlign: 'center' }}>
                Not shown if the customer opens the chat from a link with pre-filled text.
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default WelcomeChipsSettings;
